import { Explicacao } from '../../components/Explicacao'
import { TEXTOS } from '../../lib/glossario'
import type { PainelDoIad } from '../../types/iad'
import { proximaFaixa } from './pontuacao'

/**
 * A soma do mês, a faixa que ela alcança e o quanto falta para a próxima.
 *
 * **A escada inteira fica à vista, e não só a faixa atingida.** O IAD é progressivo: 10 pontos não
 * custam nada e 11 custam 5% da nota fiscal. Mostrar só "13 pontos — 5%" esconde que o próximo
 * ponto não muda nada e que o oitavo muda tudo, que é justamente o que decide se vale a pena
 * investigar mais uma ocorrência neste mês.
 *
 * @param props o painel do mês
 * @returns o cartão da pontuação
 */
export function PontuacaoDoMes({ painel }: { painel: PainelDoIad }) {
  const t = TEXTOS.iad
  const proxima = proximaFaixa(painel)
  const semDesconto = painel.faixa.redutorPercentual === 0

  return (
    <section className="cartao p-4" aria-label="Pontuação do mês">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className="text-3xl font-bold tabular-nums text-slate-900">{t.pontosDoMes(painel.pontos)}</p>
          <p className="mt-1 text-sm text-slate-600">{painel.faixa.rotuloDoTr}</p>
        </div>
        <span
          className={
            semDesconto
              ? 'rounded-full bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-900'
              : 'rounded-full bg-red-50 px-3 py-1 text-sm font-semibold text-red-900'
          }
        >
          {semDesconto ? t.semDesconto : t.desconto(painel.faixa.redutorPercentual)}
        </span>
      </div>

      <p className="mt-3 text-sm text-slate-600">
        {proxima ? t.faltamParaAProximaFaixa(painel.pontosParaAProximaFaixa, proxima.redutorPercentual) : t.naUltimaFaixa}
      </p>

      <ol className="mt-4 grid gap-1 sm:grid-cols-2" aria-label="Tabela de glosas administrativas">
        {painel.tabela.map((faixa) => (
          <li
            key={faixa.rotuloDoTr}
            className={
              faixa.rotuloDoTr === painel.faixa.rotuloDoTr
                ? 'flex items-baseline justify-between gap-2 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-900'
                : 'flex items-baseline justify-between gap-2 px-3 py-1.5 text-xs text-slate-600'
            }
            aria-current={faixa.rotuloDoTr === painel.faixa.rotuloDoTr ? 'true' : undefined}
          >
            <span>{faixa.rotuloDoTr}</span>
            <span className="tabular-nums">
              {faixa.redutorPercentual === 0 ? '—' : `−${faixa.redutorPercentual}%`}
            </span>
          </li>
        ))}
      </ol>

      <div className="mt-3">
        <Explicacao termo="ocorrenciaAdministrativa" />
      </div>
    </section>
  )
}
